import {Refresh} from '@mui/icons-material';
import {Box, Button, Card, CardContent, Chip, CircularProgress, Stack, Typography} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {UserAccount} from '../../../__generated__/resolvers-types';
import {env} from '../../../constants';
import {firebaseAuth} from '../../../setup/firebase';

export interface AuthClaimsProps {
  user: UserAccount | null | undefined;
}

interface TokenClaims {
  craftySmileRole?: string[];
  [key: string]: unknown;
}

export default function AuthClaims({user}: AuthClaimsProps) {
  const [claims, setClaims] = useState<TokenClaims | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchClaims = useCallback(async (forceRefresh = false) => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) {
      setClaims(null);
      setLoading(false);
      return;
    }

    try {
      const tokenResult = await currentUser.getIdTokenResult(forceRefresh);
      setClaims(tokenResult.claims as TokenClaims);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchClaims(true);
  }, [fetchClaims]);

  if (!user?.permissions?.includes('owner')) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Auth Claims
        </Typography>
        <Typography color="text.secondary">You do not have permission to view this page.</Typography>
      </Box>
    );
  }

  const craftySmileRole = claims?.craftySmileRole;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Auth Claims</Typography>
        <Button
          variant="contained"
          startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          Force Refresh Token
        </Button>
      </Stack>

      <Typography variant="body1" color="text.secondary" paragraph>
        View and refresh your Firebase Auth custom claims. Custom claims are used by Firestore rules for access control.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                craftySmileRole
              </Typography>
              {craftySmileRole ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {craftySmileRole.map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      color={role === 'owner' ? 'success' : role === 'admin' ? 'primary' : 'default'}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography color="error">Not set (token may need refresh after user doc update)</Typography>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                User Document Permissions
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                These are from your Firestore user document (synced to claims via userRoleTrigger):
              </Typography>
              {user?.permissions && user.permissions.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {user.permissions.map((perm) => (
                    <Chip key={perm} label={perm} variant="outlined" />
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">No permissions set</Typography>
              )}
            </CardContent>
          </Card>

          {env.isDevelopment && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  All Token Claims
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: 'grey.100',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.875rem',
                  }}
                >
                  {JSON.stringify(claims, null, 2)}
                </Box>
              </CardContent>
            </Card>
          )}

          {lastRefresh && (
            <Typography variant="caption" color="text.secondary">
              Last refreshed: {lastRefresh.toLocaleTimeString()}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}
